import { useState, useEffect, useMemo } from "react";
import { useLoader } from "@/hooks/LoaderProvider";
import { useNavigate, useParams } from "react-router-dom";
import Button from "@/components/common/Button";
import Input from "@/components/common/inputs/Input";
import { ArrowRightOutlined } from "@/config/antd";
import Avatar from "@/components/common/Avatar";
import { useDispatch, useSelector } from "react-redux";
import {
  completeInviteRegistrationAsync,
  searchUserByFieldAsync,
} from "@/store/redux/slices/user/user_slice";
import WebIcon from "@/components/common/Web_Icons";
import Success from "@/components/common/alerts/Success";
import Error from "@/components/common/alerts/Error";
import OAuthButtons from "@/components/shared/OAuth_Buttons/OAuth_Buttons";
import Logo from "@/components/common/Logo";

const Guest = () => {
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userId, organizationId } = useParams(); // Extract URL parameters

  useSelector((state) => state.user.isLoading);
  const error = useSelector((state) => state.user.error);

  // Fallback user data in case we can't fetch real data
  const fallbackUserData = useMemo(
    () => ({
      fullName: "Invited Guest",
      accountName: "Invited Guest",
      email: "guest@example.com",
      accountRole: "Guest",
    }),
    [],
  );

  // State to store actual user data
  const [userData, setUserData] = useState(fallbackUserData);
  const [isFetchingUser, setIsFetchingUser] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Fetch user data when component mounts
  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) return;

      try {
        setIsFetchingUser(true);
        const result = await dispatch(
          searchUserByFieldAsync({
            key: "id",
            value: userId,
          }),
        ).unwrap();

        if (result && result.data && result.data.length > 0) {
          const user = result.data[0];
          setUserData({
            fullName: user.fullname || fallbackUserData.fullName,
            accountName: user.fullname || fallbackUserData.accountName,
            email: user.email || fallbackUserData.email,
            accountRole:
              user.roles && user.roles.length > 0
                ? user.roles[0].charAt(0).toUpperCase() + user.roles[0].slice(1)
                : fallbackUserData.accountRole,
          });
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setFetchError(error.message || "Failed to load user data");
        // Keep fallback data
      } finally {
        setIsFetchingUser(false);
      }
    };

    fetchUserData();
  }, [userId, dispatch, fallbackUserData]);

  // Watch for error changes from redux state
  useEffect(() => {
    if (error) {
      setErrorMessage(error);
      setIsErrorVisible(true);
    }
  }, [error]);

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  const handleProceed = async () => {
    if (!password || !confirmPassword) return;

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match");
      setIsErrorVisible(true);
      return;
    }

    showLoader();

    try {
      // Call the API endpoint to complete registration
      await dispatch(
        completeInviteRegistrationAsync({
          userId,
          organizationId,
          password,
          confirmPassword,
        }),
      ).unwrap();

      hideLoader();
      setIsSuccessVisible(true);
    } catch (err) {
      hideLoader();
      setErrorMessage(err.message || "Something went wrong");
      setIsErrorVisible(true);
    }
  };

  const handleCloseSuccess = () => {
    setIsSuccessVisible(false);
    navigate("/signin");
  };

  const handleCloseError = () => {
    setIsErrorVisible(false);
  };

  return (
    <>
      {/* Top section as a standalone section - outside the flex container that AuthLayout affects */}
      <div className="fixed top-0 left-0 right-0 z-10 flex items-center justify-between p-4 md:p-6 shadow-sm">
        <div className="logo-container flex items-center">
          <Logo name="CMSLogo" size="small" />
        </div>
        <div className="flex items-center">
          <Avatar size={40} className="mr-3" alt="User" />
          <span className="font-bold text-bold text-black">
            Hello, {userData.fullName.split(" ")[0]}
          </span>
        </div>
      </div>

      {/* Main content area with padding-top to account for the fixed header */}
      <div className="flex flex-col h-screen md:h-auto pt-20">
        {/* Middle section - rest of content */}
        <div className="flex flex-col w-full max-w-md px-4 mx-auto mt-8 md:px-6">
          <div className="flex items-center mb-6">
            <Logo name="VeegilLogo" size="small" />
            <h1 className="text-left heading-large whitespace-nowrap truncate w-full text-primary">
              {isFetchingUser ? "Loading..." : `Welcome, ${userData.fullName}`}
            </h1>
          </div>

          {/* Welcome Message */}
          <div className="p-4 mb-3 rounded-lg">
            <p className="text-small text-lighterGrey">
              The majority of your information has been inputted by the Super
              Admin of your organization. All you have to do below is to setup a
              new Password for your Account
            </p>
          </div>

          {/* User Information */}
          <div className="p-4 mb-6 rounded-lg">
            <div className="flex mb-2">
              <div className="w-1/2 font-semibold text-regular truncate text-lighterGrey">
                Account Name:
              </div>
              <div className="w-1/2 text-regular text-black font-semibold">
                {isFetchingUser ? "Loading..." : userData.accountName}
              </div>
            </div>
            <div className="flex mb-2">
              <div className="w-1/2 font-semibold text-regular text-lighterGrey">
                Email Address:
              </div>
              <div className="w-1/2 text-regular text-black truncate font-semibold">
                {isFetchingUser ? "Loading..." : userData.email}
              </div>
            </div>
            <div className="flex">
              <div className="w-1/2 font-semibold text-regular text-lighterGrey">
                Account Role:
              </div>
              <div className="w-1/2 text-regular text-black font-semibold">
                {isFetchingUser ? "Loading..." : userData.accountRole}
              </div>
            </div>
          </div>

          {/* Password Fields */}
          <div className="mb-4">
            <Input
              label="CREATE PASSWORD"
              placeholder="Enter your password"
              icon={<WebIcon icon="Lock" size="small" />}
              type="password"
              value={password}
              onChange={handlePasswordChange}
              id="password"
            />
          </div>

          <p className="mb-4 text-small text-lighterGrey">
            Ensure to use Alpha-numeric and symbols combination to ensure the
            security of your new password.
          </p>

          <div className="mb-4">
            <Input
              label="CONFIRM PASSWORD"
              placeholder="Enter your password again"
              icon={<WebIcon icon="Lock" size="small" />}
              type="password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              id="confirmPassword"
            />
          </div>

          <Button
            text="Proceed"
            icon={<ArrowRightOutlined />}
            onClick={handleProceed}
            isActive={password && confirmPassword}
            color="darkBlue"
            className={`w-full ${
              !password || !confirmPassword
                ? "bg-darkBlue text-primary"
                : "bg-primary text-white"
            }`}
          />

          <div className="mt-6 text-left">
            <p className="mb-4 font-bold text-black text-small">
              Sign in using
            </p>
            <OAuthButtons />
          </div>
        </div>

        {/* Success and Error modals */}
        <Success
          isVisible={isSuccessVisible}
          onClose={handleCloseSuccess}
          header="Account Setup Successful"
          paragraph="Your account has been set up successfully. You can now sign in with your credentials."
          buttonText="Continue to Sign In"
          recoveryPath="/signin"
        />

        <Error
          isVisible={isErrorVisible}
          onClose={handleCloseError}
          header={
            fetchError ? "Failed to Load User Data" : "Account Setup Failed"
          }
          paragraph={errorMessage}
          buttonText="Try Again"
        />
      </div>
    </>
  );
};

export default Guest;
